import jsPDF from "jspdf";
import { jwtDecode } from "jwt-decode";
import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import medicGeekApi from "../../../config/axiosConfig";
import {
  showErrorAlert,
  showInfoAlert,
  showSuccessAlert,
} from "./../../../components/alerts";

const LabTestList = ({
  setCurrentForm,
  currentForm,
  setSelectedCertMedicos,
}) => {
  const [certMedicos, setCertMedicos] = useState([]);
  const [labTests, setLabTests] = useState([]);
  const [labTestsParam, setLabTestsParam] = useState([]);
  const [selectedTests, setSelectedTests] = useState([]);
  const [sortColumn, setSortColumn] = useState(""); // Columna actual para ordenar
  const [sortDirection, setSortDirection] = useState("asc"); // Dirección del ordenamiento

  const [selectedPaciente, setSelectedPaciente] = useState(null);
  const [showForm] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const labTestsPerPage = 10;
  const [selectedLabTestsLocal, setSelectedLabTestsLocal] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false); // Variable para distinguir entre editar y crear
  const [searchTerm, setSearchTerm] = useState("");
  const [formulario, setFormulario] = useState({
    fechaCertMedico: "",
    constatado: "",
    recomedacion: "",
  });

  const [pacientes, setPacientes] = useState([]);
  const [pageSize, setPageSize] = useState(10); // Inicialmente, muestra 10 registros por página

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Cantidad de elementos por página

  const [profile, setProfile] = useState({
    docNom: "",
    docEsp: "",
    docCed: "",
    docExe: "",
    docProv: "",
  });

  const [constatado, setConstatado] = useState("");
  const [recomedacion, setRecomedacion] = useState("");

  // Contador de caracteres
  const maxChars1 = 5000;

  const [profileData, setProfileData] = useState(null);

  const openModal = (labTest, customPageSize = 10) => {
    setSelectedLabTestsLocal(labTest);
    setPageSize(customPageSize);

    if (labTest) {
      setIsEditing(true);
      setFormulario({
        dynamicFields: labTest.dynamicFields || [],
        pcteId: labTest.pcteId,
        profileId: labTest.profileId,
        createdAt: labTest.createdAt,
      });
      setSelectedPaciente(labTest.paciente);
      console.log(selectedPaciente);

      // Establecer los tests seleccionados basados en labTest
      const selectedTestsFromLabTest = labTest.dynamicFields.map(
        (field) => field.AnalisisNm
      );
      setSelectedTests(selectedTestsFromLabTest); // Asegúrate de que este estado se inicializa correctamente
    } else {
      setIsEditing(false);
      setFormulario({
        dynamicFields: [],
        pcteId: null,
        profileId: null,
        createdAt: "",
      });
      setSelectedPaciente(null);
      setSelectedTests([]); // Reinicia los tests seleccionados para un nuevo registro
    }

    setModalOpen(true);
  };

  const closeModal = () => {
    setSelectedLabTestsLocal(null); // Limpia el CertMedico seleccionado
    setIsEditing(false); // Resetear el modo edición cuando se cierra el modal
    setModalOpen(false);
  };

  useEffect(() => {
    fetchProfile();
    fetchLabTestParams();
    fetchData();
  }, []);

  const handleSearchTermChange = (e) => {
    const searchTerm = e.target.value;
    setSearchTerm(searchTerm);
  };

  const fetchData = async () => {
    try {
      const labTestsResponse = await medicGeekApi.get("/labtest");
      const pacientesResponse = await medicGeekApi.get("/patients");
      if (pacientesResponse.status === 200 && labTestsResponse.status === 200) {
        const labTestsData = labTestsResponse.data;
        const pacientesData = pacientesResponse.data;

        // Guarda los pacientes en el estado
        setPacientes(pacientesData);

        // Combina los datos de labTest con los pacientes, asegurándote de comparar IDs del mismo tipo
        const combinedData = labTestsData.map((labTest) => {
          const paciente = pacientesData.find(
            (p) => Number(p.pcteId) === Number(labTest.pcteId)
          );

          return {
            ...labTest,
            pcteNom: paciente ? paciente.pcteNom : "",
            pcteNom2: paciente ? paciente.pcteNom2 : "",
            pcteApe1: paciente ? paciente.pcteApe1 : "",
            pcteApe2: paciente ? paciente.pcteApe2 : "",
            paciente: paciente || {},
          };
        });

        // Ordena los datos combinados por labTestParamId
        const sortedData = combinedData.sort(
          (a, b) => a.idLabTest - b.idLabTest
        );

        // Actualiza el estado con los datos combinados
        setLabTests(sortedData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchProfile = async () => {
    try {
      // Obtener el token desde localStorage
      const token = localStorage.getItem("token");
      if (token) {
        // Decodificar el token para obtener el nombre de usuario
        const decodedToken = jwtDecode(token);
        const username = decodedToken.username; // Asegúrate de que 'username' sea correcto

        if (username) {
          // Buscar perfil por nombre de usuario
          const response = await medicGeekApi.get(`/profile/user/${username}`);
          const data = response.data;

          // Verificar si se recibió el perfil
          if (data) {
            setProfileData(data);
            setIsEditing(true);
          } else {
            console.error("No se encontró el perfil del usuario.");
          }
        } else {
          console.error("No se pudo obtener el nombre de usuario del token.");
        }
      } else {
        console.error("No se encontró el token.");
      }
    } catch (error) {
      console.error("Error fetching data:", error.message);
    }
  };

  const fetchLabTestParams = async () => {
    try {
      const response = await medicGeekApi.get("/labtestparam");
      if (response.status === 200) {
        setLabTestsParam(response.data);
      }
    } catch (error) {
      console.error("Error fetching lab test params:", error);
    }
  };

  const sortData = (column) => {
    const isAscending = sortColumn === column && sortDirection === "asc";

    // Define the sorting function
    const sortFunction = (a, b) => {
      if (column === "idLabTest") {
        // Convert to number for correct numeric sorting
        return isAscending ? a - b : b - a;
      } else if (column === "createdAt") {
        // Handle date sorting
        return isAscending
          ? new Date(a) - new Date(b)
          : new Date(b) - new Date(a);
      } else {
        // Handle string sorting
        return isAscending ? a.localeCompare(b) : b.localeCompare(a);
      }
    };

    // Sort the data
    const sortedData = [...labTests].sort((a, b) =>
      sortFunction(a[column], b[column])
    );

    setLabTests(sortedData); // Update the state with the sorted data
    setSortColumn(column);
    setSortDirection(isAscending ? "desc" : "asc");
  };

  const activeLabTests = labTestsParam.filter(
    (param) => param.estatus === "Activo"
  );

  // Función para manejar el cambio de los checkboxes
  const handleCheckboxChange = (analisisNm) => {
    if (selectedTests.includes(analisisNm)) {
      // Si el test ya está seleccionado, lo quitamos
      setSelectedTests(selectedTests.filter((test) => test !== analisisNm));
    } else {
      // Si el test no está seleccionado, lo añadimos
      setSelectedTests([...selectedTests, analisisNm]);
    }
  };

  const pageCount = Math.ceil(labTests.length / labTestsPerPage);

  // Filtro para los pacientes en funcion de termino de busqueda
  const filteredPacientes = pacientes.filter((paciente) => {
    const fullName =
      `${paciente.pcteNom} ${paciente.pcteNom2} ${paciente.pcteApe1} ${paciente.pcteApe2}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });

  const handlePageChange = ({ selected }) => {
    setPageNumber(selected);
  };

  const displayedlabTests = labTests
    .filter((paciente) => {
      const searchWords = searchTerm.toLowerCase().split(" ");

      return searchWords.some(
        (word) =>
          paciente.pcteNom.toLowerCase().includes(word) ||
          paciente.pcteNom2.toLowerCase().includes(word) ||
          paciente.pcteApe1.toLowerCase().includes(word)
      );
    })
    .slice(pageNumber * labTestsPerPage, (pageNumber + 1) * labTestsPerPage);

  const guardarLabTests = () => {
    const fechaActual = new Date();
    const dia = String(fechaActual.getDate()).padStart(2, "0");
    const mes = String(fechaActual.getMonth() + 1).padStart(2, "0"); // Meses empiezan en 0
    const año = fechaActual.getFullYear();
    const ampm = fechaActual.getHours() >= 12 ? "PM" : "AM";
    let horas = fechaActual.getHours() % 12;
    horas = horas === 0 ? 12 : horas; // Si es 0, establece 12
    const minutos = String(fechaActual.getMinutes()).padStart(2, "0");

    const fechaHoraFormateada = `${dia}/${mes}/${año} ${horas}:${minutos} ${ampm}`;

    // Recopilar los análisis seleccionados
    const selectedDynamicFields = labTestsParam
      .filter((test) => selectedTests.includes(test.AnalisisNm)) // Filtrar los análisis seleccionados
      .map((test) => ({
        idLabTestParam: test.idLabTestParam,
        AnalisisNm: test.AnalisisNm,
      }));

    // Crear el objeto para enviar al backend
    const nuevoLabTests = {
      pcteId: selectedPaciente ? selectedPaciente.pcteId : null, // ID del paciente seleccionado
      dynamicFields: selectedDynamicFields, // Los análisis seleccionados
      profileId: profileData ? profileData.profileId : null, // ID del perfil del usuario
      createdAt: fechaHoraFormateada, // Fecha y hora actuales
    };

    // Enviar los datos al backend
    medicGeekApi
      .post("/labtest", nuevoLabTests)
      .then((response) => {
        closeModal();
        showSuccessAlert("Análisis guardado con éxito.");
        fetchData(); // Refresca los datos después de guardar
      })
      .catch((error) => {
        showErrorAlert("Error al guardar los análisis.");
        // console.error("Error al guardar los análisis", error);
      });
  };

  const areDynamicFieldsEqual = (fields1, fields2) => {
    // Verificar si ambos arrays tienen el mismo número de elementos
    if (fields1.length !== fields2.length) return false;

    // Obtener solo los nombres de los análisis
    const getNames = (fields) => fields.map((field) => field.AnalisisNm).sort();

    // Comparar los arrays de nombres ordenados
    const sortedNames1 = getNames(fields1);
    const sortedNames2 = getNames(fields2);

    return sortedNames1.every((name, index) => name === sortedNames2[index]);
  };

  const editLabTests = () => {
    if (selectedLabTestsLocal && selectedLabTestsLocal.idLabTest) {
      const { idLabTest, dynamicFields: originalDynamicFields } =
        selectedLabTestsLocal;

      // Completar `originalDynamicFields` con `isSelected` por defecto
      const originalDynamicFieldsComplete = originalDynamicFields.map(
        (field) => ({
          AnalisisNm: field.AnalisisNm,
          isSelected: field.isSelected !== undefined ? field.isSelected : false,
        })
      );

      // Recopilar los análisis seleccionados
      const selectedDynamicFields = labTestsParam
        .filter((test) => selectedTests.includes(test.AnalisisNm)) // Filtrar los análisis seleccionados
        .map((test) => ({
          AnalisisNm: test.AnalisisNm,
          isSelected: selectedTests.includes(test.AnalisisNm),
        }));

      // Ordenar y comparar los arrays
      const hasChanges = !areDynamicFieldsEqual(
        originalDynamicFieldsComplete,
        selectedDynamicFields
      );

      const fechaActual = new Date();
      const dia = String(fechaActual.getDate()).padStart(2, "0");
      const mes = String(fechaActual.getMonth() + 1).padStart(2, "0"); // Meses empiezan en 0
      const año = fechaActual.getFullYear();
      const ampm = fechaActual.getHours() >= 12 ? "PM" : "AM";
      let horas = fechaActual.getHours() % 12;
      horas = horas === 0 ? 12 : horas; // Si es 0, establece 12
      const minutos = String(fechaActual.getMinutes()).padStart(2, "0");

      const fechaHoraFormateada = `${dia}/${mes}/${año} ${horas}:${minutos} ${ampm}`;

      if (hasChanges) {
        // Crear el objeto para enviar al backend
        const newLabTests = {
          pcteId: selectedPaciente ? selectedPaciente.pcteId : null, // ID del paciente seleccionado
          profileId: profileData ? profileData.profileId : null, // ID del perfil del usuario
          dynamicFields: selectedDynamicFields, // Los análisis seleccionados
          createdAt: fechaHoraFormateada, // Fecha y hora actuales
        };

        // Mostrar alerta antes de realizar la solicitud
        showSuccessAlert("Análisis creado con éxito.");

        // Realiza una solicitud POST para crear un nuevo registro en el servidor
        medicGeekApi
          .post("/labtest", newLabTests)
          .then((response) => {
            closeModal();
            showSuccessAlert("Análisis creado con éxito.");
            fetchData(); // Refresca los datos después de crear el nuevo registro
          })
          .catch((error) => {
            showErrorAlert("Error al crear el análisis.");
            console.error("Error al crear el análisis", error);
          });
      } else {
        showInfoAlert("No se han detectado cambios.");
      }
    } else {
      showErrorAlert("No se puede editar: registro no válido.");
    }
  };

  const handlePacienteSelection = (paciente) => {
    setSelectedPaciente(paciente);
    setModalOpen(false); // Cierra el modal actual
    openPacienteModal(); // Abre el nuevo modal para el paciente seleccionado
  };

  const openPacienteModal = () => {
    setModalOpen(true);
  };

  function convertirFechaLarga(fechaStr) {
    const opcionesFecha = { day: "numeric", month: "long", year: "numeric" };
    const opcionesHora = { hour: "numeric", minute: "numeric", hour12: true };

    // Convertir la cadena de fecha a un objeto Date
    const fecha = new Date(fechaStr);

    // Obtener partes de la fecha
    const fechaLarga = new Intl.DateTimeFormat("es-ES", opcionesFecha).format(
      fecha
    );
    const horaLarga = new Intl.DateTimeFormat("es-ES", opcionesHora).format(
      fecha
    );

    // Construir la cadena en formato largo
    const partesFecha = fechaLarga.split(" de ");
    const dia = partesFecha[0];
    const mes = partesFecha[1];
    const año = partesFecha[2];

    return `día ${dia} del mes de ${mes} del año ${año} a las ${horaLarga}`;
  }

  const fechaStr = "6/6/2024 9:44 AM";
  const fechaLarga = convertirFechaLarga(fechaStr);

  const previsualizarPDF = async (certMedico) => {
    const doc = new jsPDF();

    // Define las variables de estilo
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    const additionalMargin = 25; // Margen adicional de altura
    const fontSize = 12;

    doc.setFontSize(fontSize);

    const fechaLarga = convertirFechaLarga(certMedico.certifi_fe);

    const detalleText = `
    CERTIFICADO MEDICO
    `;

    const profileText = `
Yo, ${profileData.docNom}, Médico ${profileData.docEsp} CED. ${profileData.docCed}, provisto del correspondiente exequátur No. ${profileData.docExe} Certifico examinado a ${certMedico.pcteNom} ${certMedico.pcteNom2} ${certMedico.pcteApe1} ${certMedico.pcteApe2} Cédula de identidad y electoral No. ${selectedPaciente.pcteNumDoc}
    `;

    const certificarText = `
Certifico:
${certMedico.constatado}
  `;

    const recomendarText = `
Por lo que recomiendo:
${certMedico.recomedacion}
`;

    const footerText = `
${profileData.docDirCert} a petición del interesado, hoy ${fechaLarga}`;

    const contenido = `
  ${detalleText}
  ${profileText}
  ${certificarText}
  ${recomendarText}
    `;

    const lines = doc.splitTextToSize(contenido, pageWidth - 2 * margin);
    let y = margin + additionalMargin; // Coordenada y inicial con margen adicional
    const lineHeight = fontSize * 0.6;

    // Agrega el contenido línea por línea respetando los saltos de línea
    lines.forEach((line) => {
      if (
        y + lineHeight >
        pageHeight - margin - additionalMargin - lineHeight
      ) {
        doc.addPage();
        y = margin + additionalMargin; // Resetea la coordenada y en la nueva página con margen adicional
      }
      doc.text(line, margin, y);
      y += lineHeight;
    });

    // Asegúrate de que el footerText no se divida entre páginas
    const footerLines = doc.splitTextToSize(footerText, pageWidth - 2 * margin);
    if (
      y + footerLines.length * lineHeight >
      pageHeight - margin - additionalMargin
    ) {
      doc.addPage();
      y = margin + additionalMargin;
    }

    // Agrega el footerText
    footerLines.forEach((line) => {
      doc.text(line, margin, y);
      y += lineHeight;
    });

    // Abre una ventana emergente para previsualizar el PDF
    window.open(doc.output("bloburl"), "_blank");
  };

  return (
  <div className="pro-container">

    {/* ===================================================== */}
    {/* HEADER */}
    {/* ===================================================== */}

    <div className="pro-header">

      <div className="pro-header-left">

        <h1 className="pro-title">
          Analítica
        </h1>

        <p className="pro-subtitle">
          Gestión de pruebas y análisis clínicos
        </p>

      </div>

      <div className="pro-actions">

        <div className="pro-search">

          <i className="bi bi-search"></i>

          <input
            type="search"
            placeholder="Buscar paciente..."
            value={searchTerm}
            onChange={handleSearchTermChange}
          />

        </div>

        <button
          type="button"
          className="pro-btn-primary"
          title="Crear Nueva Analítica"
          onClick={() => openModal(null, 10)}
        >

          <i className="bi bi-plus-circle-fill"></i>

          Nueva Analítica

        </button>

      </div>

    </div>

    {/* ===================================================== */}
    {/* TABLE */}
    {/* ===================================================== */}

    <div className="pro-card">

      <div className="table-responsive">

        <table className="table pro-table align-middle">

          <thead>

            <tr>

              <th onClick={() => sortData("idLabTest")}>
                ID
                {" "}
                {sortColumn === "idLabTest" &&
                  (sortDirection === "asc"
                    ? " 🔼"
                    : " 🔽")}
              </th>

              <th onClick={() => sortData("pcteNom")}>
                Paciente
                {" "}
                {sortColumn === "pcteNom" &&
                  (sortDirection === "asc"
                    ? " 🔼"
                    : " 🔽")}
              </th>

              <th onClick={() => sortData("dynamicFields")}>
                Nombre del Análisis
                {" "}
                {sortColumn === "dynamicFields" &&
                  (sortDirection === "asc"
                    ? " 🔼"
                    : " 🔽")}
              </th>

              <th onClick={() => sortData("createdAt")}>
                Fecha
                {" "}
                {sortColumn === "createdAt" &&
                  (sortDirection === "asc"
                    ? " 🔼"
                    : " 🔽")}
              </th>

              <th className="text-center">
                Acción
              </th>

            </tr>

          </thead>

          <tbody>

            {labTests.map((test) => (

              <tr key={test.idLabTest}>

                <td>

                  <span className="expediente-badge">
                    #{test.idLabTest}
                  </span>

                </td>

                <td>

                  <div className="patient-info">

                    <div className="patient-avatar">

                      {test.pcteNom?.charAt(0)}

                    </div>

                    <div>

                      <div className="patient-name">

                        {test.paciente
                          ? `${test.pcteNom} ${test.pcteApe1}`
                          : "N/A"}

                      </div>

                    </div>

                  </div>

                </td>

                <td
                  style={{
                    maxWidth: "350px",
                  }}
                  className="truncate-text"
                  title={
                    test.dynamicFields
                      .map((field) => field.AnalisisNm)
                      .join(", ")
                  }
                >

                  {test.dynamicFields &&
                  test.dynamicFields.length > 0
                    ? test.dynamicFields
                        .map((field) => field.AnalisisNm)
                        .join(", ").length > 50
                      ? `${test.dynamicFields
                          .map((field) => field.AnalisisNm)
                          .join(", ")
                          .slice(0, 50)}...`
                      : test.dynamicFields
                          .map((field) => field.AnalisisNm)
                          .join(", ")
                    : "N/A"}

                </td>

                <td>

                  {test.createdAt || "N/A"}

                </td>

                <td>

                  <div className="pro-table-actions">

                    <button
                      type="button"
                      className="pro-action-btn primary"
                      title="Editar"
                      onClick={() => openModal(test)}
                    >

                      <i className="bi bi-pencil-square"></i>

                    </button>

                  </div>

                </td>

              </tr>

            ))}

          </tbody>

        </table>

      </div>

      {/* ===================================================== */}
      {/* PAGINATION */}
      {/* ===================================================== */}

      {!showForm && (

        <div className="pro-pagination">

          <nav>

            <ul className="pagination modern-pagination">

              <li
                className={`page-item ${
                  pageNumber === 0
                    ? "disabled"
                    : ""
                }`}
              >

                <span
                  className="page-link"
                  onClick={() =>
                    handlePageChange({
                      selected: pageNumber - 1,
                    })
                  }
                >

                  &laquo;

                </span>

              </li>

              {Array.from(
                {
                  length: Math.min(pageCount, 10),
                },
                (_, index) => (

                  <li
                    key={index}
                    className={`page-item ${
                      pageNumber === index
                        ? "active"
                        : ""
                    }`}
                  >

                    <span
                      className="page-link"
                      onClick={() =>
                        handlePageChange({
                          selected: index,
                        })
                      }
                    >

                      {index + 1}

                    </span>

                  </li>

                )
              )}

              <li
                className={`page-item ${
                  pageNumber === pageCount - 1
                    ? "disabled"
                    : ""
                }`}
              >

                <span
                  className="page-link"
                  onClick={() =>
                    handlePageChange({
                      selected: pageNumber + 1,
                    })
                  }
                >

                  &raquo;

                </span>

              </li>

            </ul>

          </nav>

        </div>

      )}

    </div>

    {/* ===================================================== */}
    {/* MODAL */}
    {/* ===================================================== */}

    <Modal
      isOpen={modalOpen}
      toggle={closeModal}
      centered
      modalClassName="pro-modal-xl"
    >

      <ModalHeader toggle={closeModal}>

        {isEditing
          ? "Editar Prueba de Laboratorio"
          : "Nueva Analítica"}

      </ModalHeader>

      <ModalBody>

        {selectedPaciente ? (

          <div className="row g-4">

            {/* LEFT */}
            <div className="col-12 col-xl-4">

              <div className="pro-form-card">

                <div className="pro-patient-profile">

                  <div className="pro-patient-avatar">

                    {selectedPaciente.pcteNom?.charAt(0)}

                  </div>

                  <div>

                    <div className="pro-patient-name">

                      {selectedPaciente.pcteNom}
                      {" "}
                      {selectedPaciente.pcteApe1}

                    </div>

                    <div className="pro-patient-meta">

                      {selectedPaciente.pcteEd}
                      {" "}
                      años
                      {" • "}
                      {selectedPaciente.pcteSx}

                    </div>

                  </div>

                </div>

                <div className="mt-4">

                  <label className="pro-label">
                    Fecha
                  </label>

                  <Input
                    className="pro-input"
                    value={formulario.fechaCertMedico}
                    disabled
                  />

                </div>

                <div className="mt-3">

                  <label className="pro-label">
                    Documento
                  </label>

                  <Input
                    className="pro-input"
                    value={selectedPaciente.pcteNumDoc}
                    disabled
                  />

                </div>

              </div>

            </div>

            {/* RIGHT */}
            <div className="col-12 col-xl-8">

              <div className="pro-form-card">

                <div className="pro-section-mini-header">

                  <h5>
                    Seleccionar Análisis
                  </h5>

                  <span>
                    {selectedTests.length}
                    {" "}
                    seleccionados
                  </span>

                </div>

                <Form className="mt-4">

                  <div className="row">

                    {activeLabTests.map((param, index) => (

                      <div
                        className="col-md-6 col-lg-4 mb-3"
                        key={index}
                      >

                        <label className="pro-check-card">

                          <input
                            type="checkbox"
                            value={param.AnalisisNm}
                            onChange={() =>
                              handleCheckboxChange(
                                param.AnalisisNm
                              )
                            }
                            checked={selectedTests.includes(
                              param.AnalisisNm
                            )}
                          />

                          <span className="checkmark"></span>

                          <span className="pro-check-label">

                            {param.AnalisisNm}

                          </span>

                        </label>

                      </div>

                    ))}

                  </div>

                </Form>

              </div>

            </div>

          </div>

        ) : (

          <div>

            <div className="pro-search-modal mb-4">

              <i className="bi bi-search"></i>

              <input
                type="text"
                placeholder="Buscar paciente..."
                value={searchTerm}
                onChange={(e) =>
                  setSearchTerm(e.target.value)
                }
              />

            </div>

            <div className="pro-patient-list">

              {filteredPacientes
                .slice(
                  (currentPage - 1) * itemsPerPage,
                  currentPage * itemsPerPage
                )
                .map((paciente) => (

                  <div
                    key={paciente.pcteId}
                    className="pro-patient-item"
                  >

                    <div className="patient-info">

                      <div className="patient-avatar">

                        {paciente.pcteNom?.charAt(0)}

                      </div>

                      <div>

                        <div className="patient-name">

                          {`${paciente.pcteNom || ""} ${
                            paciente.pcteNom2 || ""
                          } ${
                            paciente.pcteApe1 || ""
                          } ${
                            paciente.pcteApe2 || ""
                          }`.trim()}

                        </div>

                        <div className="patient-id">

                          #{paciente.pcteId}

                        </div>

                      </div>

                    </div>

                    <button
                      className="pro-btn-primary-sm"
                      onClick={() =>
                        handlePacienteSelection(
                          paciente
                        )
                      }
                    >

                      Seleccionar

                    </button>

                  </div>

                ))}

            </div>

          </div>

        )}

      </ModalBody>

      <ModalFooter>

        {selectedPaciente ? (

          <>

            <Button
              className="pro-btn-cancel"
              onClick={closeModal}
            >

              Cancelar

            </Button>

            <Button
              className="pro-btn-save"
              onClick={
                isEditing
                  ? editLabTests
                  : guardarLabTests
              }
            >

              <i className="bi bi-floppy-fill me-2"></i>

              {isEditing
                ? "Guardar Edición"
                : "Guardar"}

            </Button>

            <Button
              className="pro-btn-print"
              type="button"
            >

              <i className="bi bi-printer-fill"></i>

              Imprimir

            </Button>

          </>

        ) : (

          <Button
            className="pro-btn-cancel"
            onClick={closeModal}
          >

            Cancelar

          </Button>

        )}

      </ModalFooter>

    </Modal>

  </div>
);
};
export default LabTestList;
